import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Buildings, Users, ChalkboardTeacher, GraduationCap, Books, ArrowRight, MapPin,
  Phone, EnvelopeSimple, Sparkle, CheckCircle,
} from "@phosphor-icons/react";

const STAGE_LABELS = {
  preschool: "Maternelle", primary: "Primaire",
  lower_secondary: "Collège", upper_secondary: "Lycée",
  tertiary: "Supérieur", vocational: "Formation pro.",
};
const SYSTEM_LABELS = {
  francophone_be: "Francophone (BE)", british: "British curriculum",
  ghs: "Système ghanéen", "naija_9-3-4": "Système nigérian 9-3-4",
  kenya_cbc: "Kenyan CBC",
};
const PERIOD_LABELS = {
  trimester: "3 trimestres", semester: "2 semestres",
  term: "3 terms", quarter: "4 quarters",
};
const COUNTRY_FLAGS = {
  BF: "🇧🇫", CI: "🇨🇮", SN: "🇸🇳", ML: "🇲🇱", NE: "🇳🇪", TG: "🇹🇬", BJ: "🇧🇯",
  CM: "🇨🇲", GH: "🇬🇭", NG: "🇳🇬", KE: "🇰🇪", RW: "🇷🇼", CD: "🇨🇩", MA: "🇲🇦",
  FR: "🇫🇷", GB: "🇬🇧", US: "🇺🇸",
};

export default function PublicSchool() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/schools/${slug}/showcase`);
        setData(r.data);
      } catch (e) { toast.error(formatApiError(e)); nav("/"); }
      finally { setLoading(false); }
    })();
  }, [slug]);
  if (loading) return <div className="p-10 text-sm text-muted-foreground">Chargement…</div>;
  if (!data) return null;
  const { school, stats, grade_levels, stages_present } = data;
  const themedStyle = { "--school-primary": school.primary_color };
  const stagesLabel = stages_present.map(s => STAGE_LABELS[s] || s).join(" · ");

  return (
    <div className="min-h-screen bg-background text-foreground" style={themedStyle}>
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="btn-home">
            ← Kosyam
          </Link>
          {school.accepting_applications && (
            <Link to={`/candidater/${school.id}`}>
              <Button className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full" data-testid="btn-apply">
                Admission <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${school.primary_color}12, ${school.primary_color}05)` }}>
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10 items-center">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 rounded-xl border-2 flex items-center justify-center overflow-hidden font-heading font-bold text-2xl"
                   style={{ borderColor: school.primary_color, background: school.primary_color, color: "white" }}>
                {school.logo_url ? <img src={school.logo_url} alt="Logo" className="w-full h-full object-cover" /> :
                  (school.name || "?").split(" ").map(x => x[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: school.primary_color }}>Établissement partenaire</div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  {COUNTRY_FLAGS[school.country] || "🌍"} {school.country}{school.city ? ` · ${school.city}` : ""}
                </div>
              </div>
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]" data-testid="school-name">
              {school.name}
            </h1>
            {school.motto && (
              <p className="mt-4 text-lg italic text-muted-foreground">« {school.motto} »</p>
            )}
            <p className="mt-4 text-sm text-muted-foreground">
              {stagesLabel} · Année académique <b className="text-foreground">{school.academic_year || "en cours"}</b>
            </p>
            {school.accepting_applications ? (
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-secondary bg-secondary/10">
                <CheckCircle size={14} weight="fill" /> Inscriptions ouvertes
              </div>
            ) : (
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-muted-foreground bg-muted">
                Inscriptions fermées
              </div>
            )}
          </div>

          {/* Contact card */}
          <Card className="p-5 space-y-3">
            {school.address && <div className="flex items-start gap-2 text-sm"><MapPin size={16} className="mt-0.5 shrink-0 text-muted-foreground" /><span>{school.address}</span></div>}
            {school.phone && <div className="flex items-center gap-2 text-sm"><Phone size={16} className="text-muted-foreground" /><a href={`tel:${school.phone}`} className="hover:text-primary">{school.phone}</a></div>}
            {school.email && <div className="flex items-center gap-2 text-sm"><EnvelopeSimple size={16} className="text-muted-foreground" /><a href={`mailto:${school.email}`} className="hover:text-primary">{school.email}</a></div>}
            <div className="pt-3 border-t border-border grid grid-cols-2 gap-3 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Devise</div>
                <div className="font-heading font-bold">{school.currency}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Périodes</div>
                <div className="font-heading font-bold text-sm">{PERIOD_LABELS[school.period_type] || school.period_type}</div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Élèves inscrits" v={stats.students} testid="stat-students" color={school.primary_color} />
          <StatCard icon={ChalkboardTeacher} label="Enseignants" v={stats.teachers} testid="stat-teachers" color={school.primary_color} />
          <StatCard icon={Buildings} label="Classes" v={stats.classes} testid="stat-classes" color={school.primary_color} />
          <StatCard icon={GraduationCap} label="Niveaux enseignés" v={stats.grade_levels_count} testid="stat-levels" color={school.primary_color} />
        </div>
        {stats.applications_last_30_days > 0 && (
          <div className="mt-4 text-center text-sm text-secondary flex items-center justify-center gap-2" data-testid="social-proof">
            <Sparkle size={14} weight="fill" />
            <b>{stats.applications_last_30_days} familles</b> ont candidaté ces 30 derniers jours
            {stats.accepted_families > 0 && <> · <b>{stats.accepted_families}</b> déjà admises</>}
          </div>
        )}
      </section>

      {/* Pedagogy */}
      <section className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: school.primary_color }}>Système éducatif</div>
          <div className="font-heading text-2xl font-bold">{SYSTEM_LABELS[school.education_system] || school.education_system || "—"}</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Notation sur échelle <b>{school.grading_scale}</b> · découpage en {PERIOD_LABELS[school.period_type] || school.period_type}.
          </p>
        </Card>
        <Card className="p-6">
          <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: school.primary_color }}>Étapes couvertes</div>
          <div className="flex flex-wrap gap-2">
            {stages_present.map(s => (
              <Badge key={s} className="text-xs" style={{ background: `${school.primary_color}20`, color: school.primary_color, borderColor: `${school.primary_color}40` }} data-testid={`stage-${s}`}>
                {STAGE_LABELS[s] || s}
              </Badge>
            ))}
          </div>
        </Card>
      </section>

      {/* Grade levels grid */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
          <Books size={22} weight="duotone" style={{ color: school.primary_color }} />
          Niveaux enseignés dans cet établissement
        </h2>
        {grade_levels.length === 0 ? (
          <Card className="p-10 text-center text-sm text-muted-foreground">Le catalogue des niveaux n'est pas encore configuré.</Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {grade_levels.map(g => (
              <div key={g.name} className="rounded-lg border border-border p-3 hover:border-current transition-colors" style={{ borderColor: `${school.primary_color}30` }} data-testid={`lvl-${g.code || g.name}`}>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: school.primary_color }}>{STAGE_LABELS[g.stage] || g.stage}</div>
                <div className="font-heading font-semibold text-lg mt-0.5">{g.name}</div>
                {g.code && <div className="text-[10px] font-mono text-muted-foreground">{g.code}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      {school.accepting_applications && (
        <section className="border-t border-border">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h2 className="font-heading text-3xl font-bold">Prêt à inscrire votre enfant ?</h2>
            <p className="mt-3 text-muted-foreground">La candidature se fait en ligne, en moins de 5 minutes. La direction de {school.name} vous recontactera après examen.</p>
            <Link to={`/candidater/${school.id}`} className="inline-block mt-6">
              <Button size="lg" className="rounded-full px-10 text-white" style={{ background: school.primary_color }} data-testid="btn-apply-cta">
                Admission à {school.name} <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-6 text-xs text-muted-foreground text-center">
          Propulsé par <Link to="/" className="hover:text-foreground">Kosyam</Link> · La gestion scolaire au bout des doigts
          <div className="mt-1 text-[10px] opacity-70">A <a href="https://nathortechnologies.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors" data-testid="link-nathor-public">Nathor Technologies</a> product</div>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ icon: Icon, label, v, testid, color }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{ background: `${color}15`, color }}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className="font-heading text-3xl font-bold tabular-nums">{v}</div>
      <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wide">{label}</div>
    </Card>
  );
}
